import type {
  ImportResult,
  ImportSource,
  CalendarFeed,
  CreateReminderInput,
  Reminder,
  ScheduledMessage,
  ScheduleMessageInput,
  CreatePollInput,
  CreateOfficialTeamInput,
  DirectoryTeam,
  PlatformFeatures,
  PlatformSettings,
  UpdatePlatformSettingsInput,
  ActiveSession,
  Permission,
  PermissionOverride,
  SaveOverrideInput,
  UpdateChannelInput,
  ActiveCall,
  CreateMeetingInput,
  Meeting,
  MeetingGuestInfo,
  UpdateMeetingInput,
  CallJoin,
  CallsConfig,
  AdminListQuery,
  AdminPage,
  AdminStats,
  OnlineStats,
  AdminTeam,
  AdminUpdateUserInput,
  AdminUser,
  AuditEntry,
  Release,
  SaveReleaseInput,
  SystemStatus,
  Attachment,
  AuthResponse,
  ChannelFile,
  ChannelWebhook,
  CreatedWebhook,
  CreateWebhookInput,
  CustomRole,
  SaveCustomRoleInput,
  Channel,
  ChannelNotificationsInput,
  CreateChannelInput,
  CreatedInvite,
  CreateInviteLinkInput,
  InviteLink,
  InviteLinkPreview,
  CreateInviteInput,
  CreateTaskInput,
  CreateTeamInput,
  EditMessageInput,
  ListMessagesQuery,
  ListTasksQuery,
  LoginInput,
  Me,
  Message,
  Notification,
  Paginated,
  RealtimeAuthInput,
  RealtimeAuthResponse,
  RealtimeConfig,
  ReceivedInvite,
  RegisterInput,
  SearchResults,
  SendMessageInput,
  Task,
  Team,
  TeamInvite,
  TeamMember,
  UpdatePreferencesInput,
  UpdateProfileInput,
  UpdateStatusInput,
  UserProfile,
  UpdateTaskInput,
  UpdateTeamInput,
  AdminFeedbackQuery,
  AdminTeamMember,
  AdminTeamOverview,
  AdminUserTeam,
  ChangelogEntry,
  CreateTaskColumnInput,
  TaskColumn,
  UpdateTaskColumnInput,
  CompleteSprintInput,
  CreateFeedbackInput,
  Feedback,
  UpdateFeedbackInput,
  CreateSprintInput,
  Sprint,
  StartSprintInput,
  UpdateSprintInput,
} from '@nexo/types';
import { HttpClient, type HttpClientOptions } from './http.js';
import { uploadFile, type UploadOptions } from './upload.js';

export type ApiClient = ReturnType<typeof createApiClient>;

/** Typed wrapper around every REST endpoint exposed by the server. */
export function createApiClient(options: HttpClientOptions) {
  const http = new HttpClient(options);
  const get = <T>(path: string, query?: Record<string, string | number | undefined>) =>
    http.request<T>('GET', path, { query });
  const post = <T>(path: string, body?: unknown) => http.request<T>('POST', path, { body });
  const patch = <T>(path: string, body: unknown) => http.request<T>('PATCH', path, { body });
  const put = <T>(path: string, body: unknown) => http.request<T>('PUT', path, { body });
  const del = (path: string) => http.request<undefined>('DELETE', path);

  const saveSession = (res: AuthResponse) => {
    http.tokens.set(res.tokens);
    return res.user;
  };

  return {
    http,
    auth: {
      register: (input: RegisterInput) =>
        http
          .request<AuthResponse>('POST', '/auth/register', { body: input, anonymous: true })
          .then(saveSession),
      login: (input: LoginInput) =>
        http
          .request<AuthResponse>('POST', '/auth/login', { body: input, anonymous: true })
          .then(saveSession),
      logout: async () => {
        const tokens = http.tokens.get();
        http.tokens.clear();
        if (tokens) {
          await http
            .request('POST', '/auth/logout', {
              body: { refreshToken: tokens.refreshToken },
              anonymous: true,
            })
            .catch(() => undefined);
        }
      },
      isAuthenticated: () => http.tokens.get() !== null,
      /** Id of this device's session (see `sessions`). */
      currentSessionId: () => http.tokens.get()?.sessionId ?? null,
      sessions: () => get<ActiveSession[]>('/auth/sessions'),
      revokeSession: (id: string) => del(`/auth/sessions/${id}`),
      /** Signs out every device except this one. */
      revokeOtherSessions: () => del('/auth/sessions'),
      forgotPassword: (email: string) =>
        http.request<undefined>('POST', '/auth/forgot-password', {
          body: { email },
          anonymous: true,
        }),
      resetPassword: (token: string, password: string) =>
        http.request<undefined>('POST', '/auth/reset-password', {
          body: { token, password },
          anonymous: true,
        }),
    },
    presence: {
      heartbeat: () => http.request<undefined>('POST', '/presence/heartbeat'),
      visitor: (id: string) =>
        http.request<undefined>('POST', '/presence/visitor', { body: { id }, anonymous: true }),
    },
    users: {
      me: () => get<Me>('/users/me'),
      updateMe: (input: UpdateProfileInput) => patch<Me>('/users/me', input),
      setStatus: (input: UpdateStatusInput) => put<Me>('/users/me/status', input),
      clearStatus: () => http.request<Me>('DELETE', '/users/me/status'),
      setDnd: (until: string | null) => put<Me>('/users/me/dnd', { until }),
      setPreferences: (input: UpdatePreferencesInput) => patch<Me>('/users/me/preferences', input),
      changelogSeen: (version: string) => put<Me>('/users/me/changelog', { version }),
      setAway: (away: boolean) => post<undefined>('/users/me/presence', { away }),
      /** Someone you share a team with, for their user card. */
      profile: (userId: string) => get<UserProfile>(`/users/${userId}/profile`),
    },
    account: {
      /** Everything Nexo holds about the user (GDPR), as JSON. */
      exportData: () => get<Record<string, unknown>>('/account/export'),
      /** Deletes the account; a 409 lists the teams that must be handed over first. */
      delete: (password: string) => post<undefined>('/account/delete', { password }),
    },
    teams: {
      list: () => get<Team[]>('/teams'),
      get: (teamId: string) => get<Team>(`/teams/${teamId}`),
      create: (input: CreateTeamInput) => post<Team>('/teams', input),
      update: (teamId: string, input: UpdateTeamInput) => patch<Team>(`/teams/${teamId}`, input),
      members: (teamId: string) => get<TeamMember[]>(`/teams/${teamId}/members`),
      invites: (teamId: string) => get<TeamInvite[]>(`/teams/${teamId}/invites`),
      invite: (teamId: string, input: CreateInviteInput) =>
        post<CreatedInvite>(`/teams/${teamId}/invites`, input),
      revokeInvite: (teamId: string, inviteId: string) =>
        del(`/teams/${teamId}/invites/${inviteId}`),
      updateMemberRole: (teamId: string, userId: string, role: 'admin' | 'member') =>
        patch<TeamMember>(`/teams/${teamId}/members/${userId}`, { role }),
      removeMember: (teamId: string, userId: string) => del(`/teams/${teamId}/members/${userId}`),
      leave: (teamId: string) => post<undefined>(`/teams/${teamId}/leave`),
      transferOwnership: (teamId: string, userId: string) =>
        post<undefined>(`/teams/${teamId}/transfer-ownership`, { userId }),
      setMemberRoles: (teamId: string, userId: string, roleIds: string[]) =>
        put<TeamMember>(`/teams/${teamId}/members/${userId}/roles`, { roleIds }),
      /** The person's order of their teams (team rail). */
      reorder: (teamIds: string[]) => put<Team[]>('/teams/order', { teamIds }),
      /** Brings in a Slack export (.zip), a Trello board (.json) or a Jira CSV. */
      import: (teamId: string, source: ImportSource, file: File, options: UploadOptions = {}) =>
        uploadFile<ImportResult>(http, `/teams/${teamId}/import/${source}`, file, options),
      /** Owner only: deletes the team and everything in it. */
      delete: (teamId: string) => del(`/teams/${teamId}`),
    },
    calendar: {
      feed: () => get<CalendarFeed>('/calendar/feed'),
      reset: () => post<CalendarFeed>('/calendar/feed/reset'),
    },
    reminders: {
      create: (input: CreateReminderInput) => post<Reminder>('/reminders', input),
      list: (teamId: string) => get<Reminder[]>(`/teams/${teamId}/reminders`),
      remove: (id: string) => del(`/reminders/${id}`),
    },
    scheduled: {
      create: (channelId: string, input: ScheduleMessageInput) =>
        post<ScheduledMessage>(`/channels/${channelId}/scheduled`, input),
      list: (channelId: string) => get<ScheduledMessage[]>(`/channels/${channelId}/scheduled`),
      cancel: (id: string) => del(`/scheduled/${id}`),
    },
    platform: {
      features: () => get<PlatformFeatures>('/platform/features'),
    },
    directory: {
      list: (q?: string) => get<DirectoryTeam[]>('/directory', q ? { q } : {}),
      join: (teamId: string) => post<Team>(`/directory/${teamId}/join`),
    },
    meetings: {
      list: (teamId: string) => get<Meeting[]>(`/teams/${teamId}/meetings`),
      get: (meetingId: string) => get<Meeting>(`/meetings/${meetingId}`),
      create: (input: CreateMeetingInput) => post<Meeting>('/meetings', input),
      update: (meetingId: string, input: UpdateMeetingInput) =>
        patch<Meeting>(`/meetings/${meetingId}`, input),
      cancel: (meetingId: string) => http.request<Meeting>('DELETE', `/meetings/${meetingId}`),
      respond: (meetingId: string, response: 'yes' | 'maybe' | 'no') =>
        put<Meeting>(`/meetings/${meetingId}/response`, { response }),
      /** The .ics file as text (authenticated request). */
      ics: async (meetingId: string) => {
        const response = await (options.fetch ?? globalThis.fetch)(
          http.url(`/meetings/${meetingId}/ics`),
          { headers: { Authorization: `Bearer ${http.tokens.get()?.accessToken ?? ''}` } },
        );
        if (!response.ok) throw new Error('No se pudo descargar el calendario');
        return response.text();
      },
      guestInfo: (token: string) =>
        http.request<MeetingGuestInfo>('GET', `/public/meetings/${token}`, { anonymous: true }),
      guestJoin: (token: string, name: string) =>
        http.request<CallJoin>('POST', `/public/meetings/${token}/join`, {
          anonymous: true,
          body: { name },
        }),
    },
    calls: {
      config: () => get<CallsConfig>('/calls/config'),
      join: (channelId: string) => post<CallJoin>(`/channels/${channelId}/call`),
      active: (teamId: string) => get<ActiveCall[]>(`/teams/${teamId}/calls`),
      /** Tells the others in the call that it is (no longer) recorded for an AI summary. */
      setRecording: (channelId: string, on: boolean) =>
        post<undefined>(`/channels/${channelId}/call-summaries/recording`, { on }),
      /** The call's audio; the summary is posted in the conversation when ready. */
      submitSummary: (channelId: string, audio: File, options: UploadOptions = {}) =>
        uploadFile<undefined>(http, `/channels/${channelId}/call-summaries`, audio, options),
    },
    roles: {
      list: (teamId: string) => get<CustomRole[]>(`/teams/${teamId}/roles`),
      create: (teamId: string, input: SaveCustomRoleInput) =>
        post<CustomRole>(`/teams/${teamId}/roles`, input),
      update: (roleId: string, input: SaveCustomRoleInput) =>
        patch<CustomRole>(`/roles/${roleId}`, input),
      remove: (roleId: string) => del(`/roles/${roleId}`),
      /** Permissions of the base role «Todos». */
      setEveryone: (teamId: string, permissions: Permission[]) =>
        put<undefined>(`/teams/${teamId}/everyone-permissions`, { permissions }),
    },
    inviteLinks: {
      list: (teamId: string) => get<InviteLink[]>(`/teams/${teamId}/invite-links`),
      create: (teamId: string, input: CreateInviteLinkInput) =>
        post<InviteLink>(`/teams/${teamId}/invite-links`, input),
      revoke: (teamId: string, linkId: string) => del(`/teams/${teamId}/invite-links/${linkId}`),
      preview: (token: string) => get<InviteLinkPreview>(`/join/${token}`),
      join: (token: string) => post<Team>(`/join/${token}`),
    },
    invites: {
      mine: () => get<ReceivedInvite[]>('/invites'),
      preview: (token: string) => get<ReceivedInvite>(`/invites/token/${token}`),
      accept: (inviteId: string) => post<Team>(`/invites/${inviteId}/accept`),
      decline: (inviteId: string) => post<undefined>(`/invites/${inviteId}/decline`),
    },
    channels: {
      list: (teamId: string) => get<Channel[]>(`/teams/${teamId}/channels`),
      create: (teamId: string, input: CreateChannelInput) =>
        post<Channel>(`/teams/${teamId}/channels`, input),
      openDm: (teamId: string, userId: string) => post<Channel>(`/teams/${teamId}/dms`, { userId }),
      setNotifications: (channelId: string, input: ChannelNotificationsInput) =>
        put<undefined>(`/channels/${channelId}/notifications`, input),
      markRead: (channelId: string) => post<undefined>(`/channels/${channelId}/read`),
      update: (channelId: string, input: UpdateChannelInput) =>
        patch<undefined>(`/channels/${channelId}`, input),
      remove: (channelId: string) => del(`/channels/${channelId}`),
      overrides: (channelId: string) =>
        get<PermissionOverride[]>(`/channels/${channelId}/permissions`),
      /** Sets one override; with nothing allowed or denied it is removed. */
      saveOverride: (channelId: string, input: SaveOverrideInput) =>
        put<undefined>(`/channels/${channelId}/permissions`, input),
      typing: (channelId: string, socketId?: string) =>
        post<undefined>(`/channels/${channelId}/typing`, { socketId }),
    },
    messages: {
      createPoll: (channelId: string, input: CreatePollInput) =>
        post<Message>(`/channels/${channelId}/polls`, input),
      vote: (messageId: string, optionIds: string[]) =>
        put<Message>(`/messages/${messageId}/poll/vote`, { optionIds }),
      closePoll: (messageId: string) => post<Message>(`/messages/${messageId}/poll/close`),
      list: (channelId: string, query: ListMessagesQuery = {}) =>
        get<Paginated<Message>>(`/channels/${channelId}/messages`, query),
      send: (channelId: string, input: SendMessageInput) =>
        post<Message>(`/channels/${channelId}/messages`, input),
      edit: (messageId: string, input: EditMessageInput) =>
        patch<Message>(`/messages/${messageId}`, input),
      remove: (messageId: string) => del(`/messages/${messageId}`),
      get: (messageId: string) => get<Message>(`/messages/${messageId}`),
      replies: (messageId: string) => get<Message[]>(`/messages/${messageId}/replies`),
      react: (messageId: string, emoji: string, on: boolean) =>
        on
          ? put<Message>(`/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`, {})
          : http.request<Message>(
              'DELETE',
              `/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`,
            ),
      pin: (messageId: string, pinned: boolean) =>
        pinned
          ? put<Message>(`/messages/${messageId}/pin`, {})
          : http.request<Message>('DELETE', `/messages/${messageId}/pin`),
      pins: (channelId: string) => get<Message[]>(`/channels/${channelId}/pins`),
      save: (messageId: string, saved: boolean) =>
        saved
          ? put<undefined>(`/messages/${messageId}/save`, {})
          : del(`/messages/${messageId}/save`),
      saved: (teamId: string) => get<Message[]>(`/teams/${teamId}/saved`),
      savedIds: (teamId: string) => get<string[]>(`/teams/${teamId}/saved/ids`),
    },
    tasks: {
      list: (teamId: string, query: ListTasksQuery = {}) =>
        get<Task[]>(`/teams/${teamId}/tasks`, query),
      create: (teamId: string, input: CreateTaskInput) =>
        post<Task>(`/teams/${teamId}/tasks`, input),
      update: (taskId: string, input: UpdateTaskInput) => patch<Task>(`/tasks/${taskId}`, input),
      remove: (taskId: string) => del(`/tasks/${taskId}`),
    },
    columns: {
      list: (teamId: string) => get<TaskColumn[]>(`/teams/${teamId}/columns`),
      create: (teamId: string, input: CreateTaskColumnInput) =>
        post<TaskColumn>(`/teams/${teamId}/columns`, input),
      update: (columnId: string, input: UpdateTaskColumnInput) =>
        patch<TaskColumn>(`/columns/${columnId}`, input),
      reorder: (teamId: string, ids: string[]) =>
        put<TaskColumn[]>(`/teams/${teamId}/columns/order`, { ids }),
      remove: (columnId: string, moveTo: string) =>
        http.request<undefined>('DELETE', `/columns/${columnId}`, { query: { moveTo } }),
    },
    sprints: {
      list: (teamId: string) => get<Sprint[]>(`/teams/${teamId}/sprints`),
      create: (teamId: string, input: CreateSprintInput) =>
        post<Sprint>(`/teams/${teamId}/sprints`, input),
      update: (sprintId: string, input: UpdateSprintInput) =>
        patch<Sprint>(`/sprints/${sprintId}`, input),
      start: (sprintId: string, input: StartSprintInput) =>
        post<Sprint>(`/sprints/${sprintId}/start`, input),
      complete: (sprintId: string, input: CompleteSprintInput) =>
        post<Sprint>(`/sprints/${sprintId}/complete`, input),
      remove: (sprintId: string) => del(`/sprints/${sprintId}`),
    },
    notifications: {
      list: () => get<Notification[]>('/notifications'),
      markRead: (id: string) => post<undefined>(`/notifications/${id}/read`),
      markAllRead: () => post<undefined>('/notifications/read-all'),
    },
    attachments: {
      config: () =>
        http.request<{ enabled: boolean; maxBytes: number }>('GET', '/attachments/config', {
          anonymous: true,
        }),
      upload: (channelId: string, file: File, options: UploadOptions = {}) =>
        uploadFile<Attachment>(http, `/channels/${channelId}/attachments`, file, options),
      list: (channelId: string) => get<ChannelFile[]>(`/channels/${channelId}/attachments`),
    },
    webhooks: {
      list: (channelId: string) => get<ChannelWebhook[]>(`/channels/${channelId}/webhooks`),
      create: (channelId: string, input: CreateWebhookInput) =>
        post<CreatedWebhook>(`/channels/${channelId}/webhooks`, input),
      regenerate: (webhookId: string) => post<CreatedWebhook>(`/webhooks/${webhookId}/regenerate`),
      remove: (webhookId: string) => del(`/webhooks/${webhookId}`),
    },
    releases: {
      changelog: () =>
        http.request<ChangelogEntry[]>('GET', '/releases/changelog', { anonymous: true }),
      latest: (channel: 'stable' | 'beta' = 'stable') =>
        http.request<Release | null>('GET', '/releases/latest', {
          query: { channel },
          anonymous: true,
        }),
    },
    admin: {
      stats: () => get<AdminStats>('/admin/stats'),
      online: (minutes = 60) => get<OnlineStats>('/admin/online', { minutes }),
      users: (query: AdminListQuery = {}) => get<AdminPage<AdminUser>>('/admin/users', query),
      updateUser: (userId: string, input: AdminUpdateUserInput) =>
        patch<AdminUser>(`/admin/users/${userId}`, input),
      deleteUser: (userId: string) => del(`/admin/users/${userId}`),
      teams: (query: AdminListQuery = {}) => get<AdminPage<AdminTeam>>('/admin/teams', query),
      deleteTeam: (teamId: string) => del(`/admin/teams/${teamId}`),
      featureTeam: (teamId: string, featured: boolean) =>
        patch<undefined>(`/admin/teams/${teamId}/featured`, { featured }),
      settings: () => get<PlatformSettings>('/admin/settings'),
      updateSettings: (input: UpdatePlatformSettingsInput) =>
        patch<PlatformSettings>('/admin/settings', input),
      createOfficialTeam: (input: CreateOfficialTeamInput) =>
        post<Team>('/admin/official-team', input),
      userTeams: (userId: string) => get<AdminUserTeam[]>(`/admin/users/${userId}/teams`),
      teamMembers: (teamId: string) => get<AdminTeamMember[]>(`/admin/teams/${teamId}/members`),
      // Temporary testing-phase inspection (ADMIN_TEAM_INSPECTION): read-only and logged.
      inspectTeam: (teamId: string) => get<AdminTeamOverview>(`/admin/teams/${teamId}/inspect`),
      channelMessages: (channelId: string, before?: string) =>
        get<Paginated<Message>>(`/admin/channels/${channelId}/messages`, { before }),
      threadReplies: (messageId: string) => get<Message[]>(`/admin/messages/${messageId}/replies`),
      teamTasks: (teamId: string) => get<Task[]>(`/admin/teams/${teamId}/all-tasks`),
      releases: () => get<Release[]>('/admin/releases'),
      createRelease: (input: SaveReleaseInput) => post<Release>('/admin/releases', input),
      updateRelease: (releaseId: string, input: SaveReleaseInput) =>
        patch<Release>(`/admin/releases/${releaseId}`, input),
      deleteRelease: (releaseId: string) => del(`/admin/releases/${releaseId}`),
      audit: (page = 1) => get<AdminPage<AuditEntry>>('/admin/audit', { page }),
      system: () => get<SystemStatus>('/admin/system'),
      testEmail: () => post<{ to: string }>('/admin/system/test-email'),
      feedback: (query: AdminFeedbackQuery = {}) =>
        get<AdminPage<Feedback>>('/admin/feedback', query),
      updateFeedback: (feedbackId: string, input: UpdateFeedbackInput) =>
        patch<Feedback>(`/admin/feedback/${feedbackId}`, input),
    },
    feedback: {
      create: (input: CreateFeedbackInput) => post<Feedback>('/feedback', input),
      mine: () => get<Feedback[]>('/feedback/mine'),
    },
    realtime: {
      config: () => http.request<RealtimeConfig>('GET', '/realtime/config', { anonymous: true }),
      authorize: (input: RealtimeAuthInput) => post<RealtimeAuthResponse>('/realtime/auth', input),
    },
    search: (teamId: string, q: string) => get<SearchResults>(`/teams/${teamId}/search`, { q }),
  };
}
